import type { SVGProps } from "react";

/**
 * Prezynt line icons: 24px grid, ~1.8px stroke, rounded caps, `currentColor`.
 * Inline SVG so nothing is fetched at runtime (the previous Iconify glyphs were
 * loaded from a third-party API on every page view).
 *
 * `lineIconSvg` renders the same glyph as an HTML string for places that build
 * DOM outside React (Grid.js cell formatters).
 */
export type LineIconName =
  | "dashboard"
  | "analytics"
  | "intelligence"
  | "graph"
  | "cart-check"
  | "calendar"
  | "clock"
  | "link"
  | "list"
  | "layers"
  | "users"
  | "receipt"
  | "tag"
  | "award"
  | "bell"
  | "heart"
  | "sliders"
  | "message"
  | "book"
  | "building"
  | "menu"
  | "sidebar"
  | "chevron-down"
  | "chevron-right"
  | "sun"
  | "moon"
  | "user"
  | "log-out"
  | "arrow-left"
  | "arrow-right"
  | "pencil"
  | "trash"
  | "download"
  | "upload"
  | "eye"
  | "eye-off"
  | "rotate"
  | "search"
  | "sparkle"
  | "megaphone"
  | "info";

const PATHS: Record<LineIconName, string> = {
  dashboard:
    '<rect x="3.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.5"/>',
  analytics:
    '<path d="M4 20h16"/><path d="M6.5 16V11"/><path d="M11 16V6"/><path d="M15.5 16v-3"/><path d="M20 16V8"/>',
  intelligence:
    '<path d="M12 3.5a8.5 8.5 0 1 0 8.5 8.5H12z"/><path d="M15.5 3.9A8.5 8.5 0 0 1 20.1 8.5H15.5z"/>',
  graph:
    '<circle cx="6" cy="18" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="18" cy="18" r="2.5"/><circle cx="6" cy="6" r="2.5"/><path d="M8 7.5l8 9"/><path d="M8.5 6h7"/><path d="M6 8.5v7"/>',
  "cart-check":
    '<path d="M3 4h2l2.4 11h11.2L21 8H6.3"/><circle cx="9" cy="19.5" r="1.3"/><circle cx="17" cy="19.5" r="1.3"/><path d="m10.5 11 2 2 3.5-3.5"/>',
  calendar:
    '<rect x="3.5" y="5" width="17" height="15.5" rx="2"/><path d="M3.5 10h17"/><path d="M8 3v4"/><path d="M16 3v4"/><path d="m12 13 .9 1.9 2.1.3-1.5 1.5.4 2.1-1.9-1-1.9 1 .4-2.1-1.5-1.5 2.1-.3z"/>',
  clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>',
  link:
    '<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/>',
  list:
    '<path d="M9 7h11"/><path d="M9 12h11"/><path d="M9 17h11"/><path d="M4.5 7h.01"/><path d="M4.5 12h.01"/><path d="M4.5 17h.01"/>',
  layers:
    '<path d="m12 4 8 4.5-8 4.5-8-4.5z"/><path d="m4 13 8 4.5 8-4.5"/><path d="m4 17 8 4.5 8-4.5"/>',
  users:
    '<circle cx="9.5" cy="8" r="3.5"/><path d="M3.5 20a6 6 0 0 1 12 0"/><path d="M15.5 4.7a3.5 3.5 0 0 1 0 6.6"/><path d="M17.5 14.3a6 6 0 0 1 3 5.7"/>',
  receipt:
    '<path d="M6 3.5h12v17l-3-2-3 2-3-2-3 2z"/><path d="M9 8.5h6"/><path d="M9 12h6"/><path d="M9 15.5h4"/>',
  tag:
    '<path d="M4 4h7.5l8.5 8.5-7.5 7.5L4 11.5z"/><circle cx="8.5" cy="8.5" r="1.3"/>',
  award:
    '<circle cx="12" cy="9" r="5.5"/><path d="m8.5 13.5-1.5 7 5-2.5 5 2.5-1.5-7"/>',
  bell:
    '<path d="M6 16.5V11a6 6 0 1 1 12 0v5.5l1.5 1.5H4.5z"/><path d="M10 20.5a2 2 0 0 0 4 0"/>',
  heart:
    '<path d="M12 20s-7.5-4.6-7.5-10A4.3 4.3 0 0 1 12 7.6 4.3 4.3 0 0 1 19.5 10c0 5.4-7.5 10-7.5 10z"/>',
  sliders:
    '<path d="M4 7h10"/><path d="M18 7h2"/><circle cx="16" cy="7" r="2"/><path d="M4 17h4"/><path d="M12 17h8"/><circle cx="10" cy="17" r="2"/>',
  message:
    '<path d="M4 5.5h16v10H10l-4.5 3.5V15.5H4z"/><path d="M8 9.5h8"/><path d="M8 12.5h5"/>',
  book:
    '<path d="M5 4.5h11.5a2 2 0 0 1 2 2v13H7a2 2 0 0 1-2-2z"/><path d="M5 17.5a2 2 0 0 1 2-2h11.5"/><path d="M9 8.5h5"/>',
  building:
    '<path d="M5.5 20.5V5a1.5 1.5 0 0 1 1.5-1.5h7A1.5 1.5 0 0 1 15.5 5v15.5"/><path d="M15.5 10h3A1.5 1.5 0 0 1 20 11.5v9"/><path d="M4 20.5h16"/><path d="M9 7.5h3"/><path d="M9 11.5h3"/><path d="M9 15.5h3"/>',
  menu: '<path d="M4 7h16"/><path d="M4 12h16"/><path d="M4 17h16"/>',
  sidebar:
    '<rect x="3.5" y="4.5" width="17" height="15" rx="2"/><path d="M9.5 4.5v15"/><path d="m14 10 2 2-2 2"/>',
  "chevron-down": '<path d="m6 9.5 6 6 6-6"/>',
  "chevron-right": '<path d="m9.5 6 6 6-6 6"/>',
  sun:
    '<circle cx="12" cy="12" r="4"/><path d="M12 3v2"/><path d="M12 19v2"/><path d="M3 12h2"/><path d="M19 12h2"/><path d="m5.6 5.6 1.4 1.4"/><path d="m17 17 1.4 1.4"/><path d="m5.6 18.4 1.4-1.4"/><path d="m17 7 1.4-1.4"/>',
  moon: '<path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z"/>',
  user: '<circle cx="12" cy="8.5" r="4"/><path d="M4.5 20.5a7.5 7.5 0 0 1 15 0"/>',
  "log-out": '<path d="M10 4.5H5.5v15H10"/><path d="M14 8l4 4-4 4"/><path d="M18 12H9"/>',
  "arrow-left": '<path d="M19 12H5"/><path d="m11 6-6 6 6 6"/>',
  "arrow-right": '<path d="M5 12h14"/><path d="m13 6 6 6-6 6"/>',
  pencil:
    '<path d="m4 20 .8-4L15.6 5.2a1.8 1.8 0 0 1 2.6 0l.6.6a1.8 1.8 0 0 1 0 2.6L8 19.2z"/><path d="m14 6.8 3.2 3.2"/>',
  trash:
    '<path d="M4.5 6.5h15"/><path d="M9 6.5V4.5h6v2"/><path d="M6.5 6.5 7.3 20h9.4l.8-13.5"/><path d="M10 10.5v6"/><path d="M14 10.5v6"/>',
  download: '<path d="M12 4v11"/><path d="m7.5 10.5 4.5 4.5 4.5-4.5"/><path d="M4.5 19.5h15"/>',
  upload:
    '<path d="M12 16V6"/><path d="m7.5 10.5 4.5-4.5 4.5 4.5"/><path d="M5 15.5v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"/>',
  eye: '<path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="3"/>',
  "eye-off":
    '<path d="M3 3l18 18"/><path d="M10.6 6.2A9.7 9.7 0 0 1 12 6c6 0 9.5 6 9.5 6a16.7 16.7 0 0 1-3.2 3.8"/><path d="M6.4 6.9A15.7 15.7 0 0 0 2.5 12S6 18 12 18a9.5 9.5 0 0 0 4-.9"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/>',
  rotate: '<path d="M4 12a8 8 0 1 0 2.3-5.7"/><path d="M4 4v4.5h4.5"/>',
  search: '<circle cx="11" cy="11" r="6.5"/><path d="m16 16 4.5 4.5"/>',
  sparkle: '<path d="M12 3c.6 4.7 2.3 6.4 7 7-4.7.6-6.4 2.3-7 7-.6-4.7-2.3-6.4-7-7 4.7-.6 6.4-2.3 7-7z"/>',
  megaphone:
    '<path d="M4 9.5v5a1.5 1.5 0 0 0 1.5 1.5H8l9.5 4.5V5L8 9.5H5.5A1.5 1.5 0 0 0 4 11z"/><path d="M8 9.5V16"/><path d="M20.5 10v4"/>',
  info: '<circle cx="12" cy="12" r="8.5"/><path d="M12 11v5"/><path d="M12 7.5h.01"/>',
};

const SVG_ATTRS =
  'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"';

type LineIconProps = SVGProps<SVGSVGElement> & {
  name: LineIconName;
  size?: number;
  /** Accessible label. Omit for decorative icons next to visible text. */
  title?: string;
};

const LineIcon = ({ name, size = 18, title, ...rest }: LineIconProps) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
    role={title ? "img" : undefined}
    focusable="false"
    dangerouslySetInnerHTML={{ __html: (title ? `<title>${title}</title>` : "") + PATHS[name] }}
    {...rest}
  />
);

/** Same glyph as an HTML string, for Grid.js `html()` / `h()` cells. */
export const lineIconSvg = (name: LineIconName, size = 18) =>
  `<svg ${SVG_ATTRS} width="${size}" height="${size}" aria-hidden="true" focusable="false">${PATHS[name]}</svg>`;

export default LineIcon;
